import React from "react";
import Navigation from "./components/Navigation";
import Clock from "./components/Clock";
import Player from "./components/Player";
import AudioVisualizer from "./components/AudioVisualizer";
import SongData from "./components/SongData.json";
import Localization from "./components/Localization.json";
import Playlist from "./components/Playlist";
import TitleDisplay from "./TitleDisplay";
import LyricsDisplay from "./LyricsDisplay";
import { convertWPEColorToCSS, randomExcluded, toFilename } from "./helpers";
import Lyrics from "./components/Lyrics";
import util from "util";

const Main = () => {
  const [songIndex, setIndex] = React.useState(0);
  const [player, setPlayer] = React.useState("true");
  const [clock, setClock] = React.useState("true");
  const [audioVis, setAudioVis] = React.useState("true");
  const [mode, setMode] = React.useState(0);
  const [playlist, setPlaylist] = React.useState("true");
  const [hasLyrics, setHasLyrics] = React.useState("true");
  const [shuffle, setShuffle] = React.useState(true);
  const [replay, setReplay] = React.useState(true);
  const [wpePaused, setWPEPaused] = React.useState(false);
  const [songList, setSongList] = React.useState([[], []]);
  const [uiVolume, setUiVolume] = React.useState(0.5);
  const [textSize, setTextSize] = React.useState(1);
  const [titleDisplay, setTitleDisplay] = React.useState(TitleDisplay.English);
  const [lyricsDisplay, setLyricsDisplay] = React.useState(LyricsDisplay.Original);
  const [use24HourClock, set24HourClock] = React.useState(true);
  const [showSeconds, setShowSeconds] = React.useState(true);
  const [overrideBackgroundColor, setOverrideBackgroundColor] = React.useState(false);
  const [customBackgroundColor, setCustomBackgroundColor] = React.useState("#000000");
  const [overrideLineColor, setOverrideLineColor] = React.useState(false);
  const [customLineColor, setCustomLineColor] = React.useState("#ffffff");
  const [language, setLanguage] = React.useState("en-us");
  const audioRef = React.useRef(new Audio());

  const backgroundColor = overrideBackgroundColor ? customBackgroundColor : SongData[songIndex].backgroundColor;
  const lineColor = overrideLineColor ? customLineColor : SongData[songIndex].lineColor;

  const translate = (key, ...args) => {
    const fallbackLanguage = Localization["en-us"];
    const currentLanguage = Localization[language] ?? fallbackLanguage;
    const translation = currentLanguage[key] ?? fallbackLanguage[key];
    return util.format(translation, ...args);
  }

  const playerHandler = () => {
    //Changes and sets the music player
    setPlayer(player === "true" ? "false" : "true");
    localStorage.setItem("player", player === "true" ? "false" : "true");
  };

  const clockHandler = () => {
    //Changes and sets the clock
    setClock(clock === "true" ? "false" : "true");
    localStorage.setItem("clock", clock === "true" ? "false" : "true");
  };

  const visualizerHandler = () => {
    //Changes and sets the visualizer
    setAudioVis(audioVis === "true" ? "false" : "true");
    localStorage.setItem("audioVis", audioVis === "true" ? "false" : "true");
  };

  const playlistHandler = () => {
    //Changes and sets the playlist
    setPlaylist(playlist === "true" ? "false" : "true");
    localStorage.setItem("playlistH", playlist === "true" ? "false" : "true");
  };

  const lyricsHandler = () => {
    //Toggles the lyrics
    setHasLyrics(hasLyrics === "true" ? "false" : "true");
    localStorage.setItem("lyricsBocchi", hasLyrics === "true" ? "false" : "true");
  };

  const reShuffle = (x, y) => {
    //Replay and Shuffle
    if (x === "shuffle") {
      setShuffle(y);
      setReplay(false);
      localStorage.setItem("bocchi-14", `[${false}, ${y}]`);
    } else {
      setReplay(y);
      setShuffle(false);
      localStorage.setItem("bocchi-14", `[${y}, ${false}]`);
    }
  };

  const getKey = (m) => songList[m - 1].findIndex((x) => x === songIndex + 1);

  const changeSong = (e) => {
    //Changes the song using conditions ~from player
    const direction = e === true ? 1 : -1;
    if (
      //Default Playlist
      mode === 0
    ) {
      if (shuffle === false) {
        setIndex((songIndex + direction + SongData.length) % SongData.length);
      } else {
        setIndex(randomExcluded(0, SongData.length - 1, songIndex));
      }
    } else if (
      //Check if array is empty
      songList[mode - 1].length
    ) {
      if (shuffle === false) {
        setIndex(songList[mode - 1][(getKey(mode) + direction + songList[mode - 1].length) % songList[mode - 1].length] - 1);
      } else {
        const randomIndex = randomExcluded(0, songList[mode - 1].length - 1, getKey(mode));
        setIndex(songList[mode - 1][randomIndex] - 1);
      }
    }
  };

  const addSong = (x, y) => {
    if (songList[y - 1].includes(x)) {
      //Op check
    } else {
      let tempArray = [...songList];
      tempArray[y - 1].push(x);
      setSongList(tempArray);
      localStorage.setItem("playlistBocchi", JSON.stringify(tempArray));
    }
  };

  const removeSong = (y) => {
    songList[y - 1].splice(getKey(y), 1);
    setSongList([...songList]);
    localStorage.setItem("playlistBocchi", JSON.stringify(songList));
  };

  const changeId = (e) => {
    //Change the song through playlist
    setIndex(e);
  };

  const changeMode = (e) => {
    //Changes mode ~the playlist of what the user is using
    setMode(e);
    localStorage.setItem("mode", e);
  };

  const [prevMode, setPrevMode] = React.useState();

  if (mode !== prevMode) {
    setPrevMode(mode);
    if (mode === 0) {
      setIndex(Math.floor(SongData.length * Math.random()));
    } else if (Array.isArray(songList[mode - 1]) && songList[mode - 1].length) {
      setIndex(
        songList[mode - 1][
        Math.floor(songList[mode - 1].length * Math.random())
        ] - 1,
      );
    }
  }

  React.useEffect(() => {
    //Loads and sets data onstart
    try {
      setAudioVis(
        localStorage.getItem("audioVis") !== null
          ? localStorage.getItem("audioVis")
          : "true",
      );
      setClock(
        localStorage.getItem("clock") !== null
          ? localStorage.getItem("clock")
          : "true",
      );
      setPlayer(
        localStorage.getItem("player") !== null
          ? localStorage.getItem("player")
          : "true",
      );
      setPlaylist(
        localStorage.getItem("playlistH") !== null
          ? localStorage.getItem("playlistH")
          : "true",
      );
      setMode(
        localStorage.getItem("mode") !== null
          ? parseInt(localStorage.getItem("mode"))
          : 0,
      );
      setSongList(
        localStorage.getItem("playlistBocchi") !== null
          ? JSON.parse(localStorage.getItem("playlistBocchi"))
          : [[], []],
      );
      setHasLyrics(
        localStorage.getItem("lyricsBocchi") !== null
          ? localStorage.getItem("lyricsBocchi")
          : "true",
      );
      if (localStorage.getItem("bocchi-14") !== null) {
        let temp14 = JSON.parse(localStorage.getItem("bocchi-14"));
        setReplay(temp14[0]);
        setShuffle(temp14[1]);
      } else {
        setReplay(false);
        setShuffle(true);
      }
    } catch (e) {
      setAudioVis("true");
      localStorage.setItem("audioVis", "true");
      setClock("true");
      localStorage.setItem("clock", "true");
      setPlayer("true");
      localStorage.setItem("player", "true");
      setPlaylist("true");
      localStorage.setItem("playlistH", "true");
      setMode(0);
      localStorage.setItem("mode", 0);
      setSongList([[], []]);
      localStorage.setItem("playlistBocchi", JSON.stringify([[], []]));
      setReplay(false);
      setShuffle(true);
      localStorage.setItem("bocchi-14", JSON.stringify([true, false]));
      localStorage.setItem("lyricsBocchi", "true");
    }
  }, []);

  //Wallpaper Engine Functions
  try {
    window.wallpaperPropertyListener = {
      applyUserProperties: function (properties) {
        if (properties.uiVolume) setUiVolume(properties.uiVolume.value * 0.1);
        if (properties.textsize) setTextSize(properties.textsize.value / 10);
        if (properties.titledisplay) setTitleDisplay(properties.titledisplay.value)
        if (properties.lyricsdisplay) setLyricsDisplay(properties.lyricsdisplay.value)
        if (properties.use24hourclock) set24HourClock(properties.use24hourclock.value)
        if (properties.showseconds) setShowSeconds(properties.showseconds.value)
        if (properties.overridebackgroundcolor) setOverrideBackgroundColor(properties.overridebackgroundcolor.value)
        if (properties.custombackgroundcolor) setCustomBackgroundColor(convertWPEColorToCSS(properties.custombackgroundcolor.value))
        // Wallpaper Engine properties are named with "accent" instead of "line" as the color is being used in places other than border lines
        // TODO: rename line color to accent color in another refactor
        if (properties.overrideaccentcolor) setOverrideLineColor(properties.overrideaccentcolor.value)
        if (properties.customaccentcolor) setCustomLineColor(convertWPEColorToCSS(properties.customaccentcolor.value, .9))
      },
      applyGeneralProperties: function (properties) {
        if (properties.language) setLanguage(properties.language);
      },
      setPaused: function (isPaused) {
        setWPEPaused(isPaused);
      },
    };
  } catch (e) {
    console.log(e);
  }

  return (
    <div
      className="Main"
      style={{ backgroundColor: backgroundColor }}
    >
      {audioVis === "true" ? (
        <AudioVisualizer lineColor={lineColor} />
      ) : null}
      <img
        className="mainImage"
        src={`./assets/covers/${
          toFilename(SongData[songIndex].single ?? SongData[songIndex].album ?? SongData[songIndex].name)
        }.jpg`}
        alt=""
        style={{ boxShadow: "1px 1px 12px #150625" }}
      />
      <Navigation
        uiVolume={uiVolume}
        playerHandler={playerHandler}
        clockHandler={clockHandler}
        playlistHandler={playlistHandler}
        changeSong={changeSong}
        visualizerHandler={visualizerHandler}
        songIndex={songIndex}
        lyricsHandler={lyricsHandler}
      />
      {playlist === "true" ? (
        <Playlist
          textSize={textSize}
          uiVolume={uiVolume}
          songIndex={songIndex}
          changeId={changeId}
          songList={songList}
          changeMode={changeMode}
          mode={mode}
          addSong={addSong}
          removeSong={removeSong}
          titleDisplay={titleDisplay}
          backgroundColor={backgroundColor}
          lineColor={lineColor}
          translate={translate}
        />
      ) : null}
      {clock === "true" ? (
        <Clock
          textShadow={SongData[songIndex].clockTextShadow}
          textSize={textSize}
          use24HourClock={use24HourClock}
          showSeconds={showSeconds}
        />
      ) : null}
      {player === "true" ? (
        <Player
          uiVolume={uiVolume}
          playerTextShadow={SongData[songIndex].playerTextShadow}
          songIndex={songIndex}
          changeSong={changeSong}
          shuffle={shuffle}
          replay={replay}
          reShuffle={reShuffle}
          textSize={textSize}
          titleDisplay={titleDisplay}
          audioRef={audioRef}
          wpePaused={wpePaused}
        />
      ) : null}
      {
        hasLyrics === "true" && (
          <Lyrics
            songIndex={songIndex}
            audioRef={audioRef}
            uiVolume={uiVolume}
            lyricsDisplay={lyricsDisplay}
            backgroundColor={backgroundColor}
            lineColor={lineColor}
          />
        )
      }
   </div>
  );
};

export default Main;
